import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicLayout = ({ children }) => {
  const [menus, setMenus] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await axios.get(`${API}/menus`);
        setMenus(response.data);
      } catch (error) {
        console.error('Failed to fetch menus:', error);
      }
    };
    fetchMenus();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'header-scrolled py-3' : 'bg-white py-4'
        }`}
        data-testid="public-header"
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center">
            {/* Logo */}
            <a 
              href="https://wingsdiet.com/"
              className="flex items-center group mr-10"
              data-testid="logo-link"
            >
              <img 
                src="/logo.png" 
                alt="WingsDiet" 
                className="h-8 w-auto"
              />
            </a>

            {/* Desktop Navigation - Next to Logo */}
            <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
              {menus.map((menu) => (
                <a
                  key={menu.id}
                  href={menu.url}
                  className={`font-medium text-base transition-colors ${
                    (menu.url === '/' || menu.url === '/blog') && (location.pathname === '/' || location.pathname === '/blog')
                      ? 'text-[#4A7BF7]' 
                      : 'text-[#1a1a1a] hover:text-[#4A7BF7]'
                  }`}
                >
                  {menu.name}
                </a>
              ))}
            </nav>

            {/* Spacer to push mobile button to right */}
            <div className="flex-1" />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mobile-menu absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {menus.map((menu) => (
                <a
                  key={menu.id}
                  href={menu.url}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                    (menu.url === '/' || menu.url === '/blog') && (location.pathname === '/' || location.pathname === '/blog')
                      ? 'bg-[#4A7BF7]/10 text-[#4A7BF7]' 
                      : 'text-[#1a1a1a] hover:bg-slate-50'
                  }`}
                >
                  {menu.name}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#f8f9fc] text-slate-700 py-16" data-testid="public-footer">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div>
              <div className="mb-5">
                <img 
                  src="/logo.png" 
                  alt="WingsDiet" 
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-[#555555] text-sm leading-relaxed mb-6">
                WingsDiet is a revolutionary health and wellness app designed to help you eat smarter, feel better, and live healthier - no matter how busy your life is.
              </p>
              {/* Rating Card */}
              <div className="inline-flex items-center gap-3 bg-white rounded-lg px-4 py-3 shadow-sm border border-slate-100">
                <span className="text-3xl font-bold text-[#333333]">4.8</span>
                <div>
                  <div className="flex text-yellow-400 text-sm">
                    {'★★★★★'.split('').map((star, i) => (
                      <span key={i}>{star}</span>
                    ))}
                  </div>
                  <p className="text-[#555555] text-xs font-medium mt-0.5">BASED ON 204 REVIEWS ↗</p>
                </div>
              </div>
            </div>

            {/* About Product */}
            <div>
              <h4 className="font-bold mb-5 text-sm uppercase tracking-widest text-[#333333]">
                About Product
              </h4>
              <ul className="space-y-3 list-disc list-inside marker:text-slate-500">
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com" className="hover:text-[#4A7BF7] transition-colors">Home</a>
                </li>
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com#how-it-works" className="hover:text-[#4A7BF7] transition-colors">How it works</a>
                </li>
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com#downloads" className="hover:text-[#4A7BF7] transition-colors">Downloads</a>
                </li>
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com#faqs" className="hover:text-[#4A7BF7] transition-colors">FAQs</a>
                </li>
              </ul>
            </div>

            {/* Need Help */}
            <div>
              <h4 className="font-bold mb-5 text-sm uppercase tracking-widest text-[#333333]">
                Need Help?
              </h4>
              <p className="text-slate-500 text-sm mb-2">Call us directly?</p>
              <a href="tel:+1-585-438-0275" className="text-[#333333] font-bold text-base hover:text-[#4A7BF7] transition-colors">
                +1-585-438-0275
              </a>
            </div>

            {/* Keep In Touch */}
            <div>
              <h4 className="font-bold mb-5 text-sm uppercase tracking-widest text-[#333333]">
                Keep In Touch With Us
              </h4>
              <ul className="space-y-3 list-disc list-inside marker:text-slate-500">
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com/term_and_conditions.php" className="hover:text-[#4A7BF7] transition-colors">Terms &amp; Conditions</a>
                </li>
                <li className="text-slate-500 text-sm">
                  <a href="https://wingsdiet.com/privacy_policy.php" className="hover:text-[#4A7BF7] transition-colors">Privacy Policy</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-16 pt-8 border-t border-slate-200 text-center">
            <p className="text-slate-400 text-sm">
              Copyright {new Date().getFullYear()} WingsDiet. All rights reserved.
            </p>
          </div>
        </div>

        {/* Scroll to Top Button */}
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:shadow-xl transition-shadow z-40"
          aria-label="Scroll to top"
        >
          <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </footer>
    </div>
  );
};

export default PublicLayout;
